import { inject, injectable, injectFromBase } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type BaseWrapper from '@/data/models/wrappers/BaseWrapper'
import type LoggerService from '@/services/loggerService'
import type BaseWrapperService from '@/services/wrappers/baseWrapperService'
import type QueryStorageService from '@/services/storage/queryStorageService'
import TableQueryWrapperService from '@/services/wrappers/table/tableQueryWrapperService'
import type TableLocalStorageWrapperService from '@/services/wrappers/table/tableLocalStorageWrapperService'
import type { WrapperTableParams, WrapperTableParamsToSave } from '@/services/wrappers/table/types'
import type { WrapperParams } from '@/services/wrappers/types'

/**
 * queryTable wrapper layered over a lsTable.
 */
@injectable()
@injectFromBase()
export class TableQueryLsWrapperService extends TableQueryWrapperService {
  constructor(
    @inject(SERVICE_TYPES.BaseWrapperService) baseWrapperService: BaseWrapperService<BaseWrapper>,
    @inject(SERVICE_TYPES.LoggerService) loggerService: LoggerService,
    @inject(SERVICE_TYPES.QueryStorageService) storageService: QueryStorageService,
    @inject(SERVICE_TYPES.TableLocalStorageWrapperService)
    private readonly lsWrapperService: TableLocalStorageWrapperService,
  ) {
    super(baseWrapperService, loggerService, storageService)
  }

  protected override getStorageParams(wrapperId: string): WrapperParams {
    const fromUrl = super.getStorageParams(wrapperId)
    const fromLs = this.lsWrapperService.getStorageParams(wrapperId)
    return { ...fromLs, ...fromUrl }
  }

  public override async setParamsByTableData(
    wrapperId: string,
    params: WrapperTableParams,
    paramsToSave: WrapperTableParamsToSave,
    shouldReplaceState = true,
  ) {
    const result = await super.setParamsByTableData(wrapperId, params, paramsToSave, shouldReplaceState)
    this.lsWrapperService.setStorageParams(wrapperId, this.extractDataForLs(params, paramsToSave), shouldReplaceState)
    return result
  }

  private extractDataForLs(params: WrapperTableParams, paramsToSave: WrapperTableParamsToSave): WrapperTableParams {
    const pickedParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [
        key,
        this.pickByParamsToSave(value, paramsToSave[key as keyof WrapperTableParamsToSave]),
      ]),
    ) as WrapperTableParams
    return this.filterParams(pickedParams)
  }
}
